import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';
import Button from './Button';
import { useBankAccountStore } from '../../store/bankAccountStore';
import SegmentedControl from './SegmentedControl';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransfer: (destinationType: 'shared' | 'personal', destinationAccountId: string, recurringMode: 'single' | 'all_future') => void;
  sourceType: 'shared' | 'personal';
  operationCount: number;
  hasRecurring: boolean;
  currentAccountId: string | null;
  hasCouple: boolean;
  isLoading?: boolean;
}

export default function TransferModal({
  isOpen,
  onClose,
  onTransfer,
  sourceType,
  operationCount,
  hasRecurring,
  currentAccountId,
  hasCouple,
  isLoading = false,
}: TransferModalProps) {
  const { t } = useTranslation('operations');
  const { t: tc } = useTranslation('common');
  const { bankAccounts, sharedBankAccounts, fetchBankAccounts, fetchSharedBankAccounts, isLoaded, isSharedLoaded } = useBankAccountStore();

  const [destinationType, setDestinationType] = useState<'shared' | 'personal'>(sourceType);
  const [destinationAccountId, setDestinationAccountId] = useState('');
  const [recurringMode, setRecurringMode] = useState<'single' | 'all_future'>('single');

  // Charger les comptes si nécessaire
  useEffect(() => {
    if (!isOpen) return;
    if (!isLoaded) fetchBankAccounts();
    if (!isSharedLoaded && hasCouple) fetchSharedBankAccounts();
  }, [isOpen, isLoaded, isSharedLoaded, hasCouple, fetchBankAccounts, fetchSharedBankAccounts]);

  // Reset le formulaire quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setDestinationType(sourceType);
      setDestinationAccountId('');
      setRecurringMode('single');
    }
  }, [isOpen, sourceType]);

  // Filtrer les comptes disponibles
  const availableAccounts = destinationType === 'shared'
    ? sharedBankAccounts.filter(a => !(destinationType === sourceType && a.id === currentAccountId))
    : bankAccounts.filter(a => !(destinationType === sourceType && a.id === currentAccountId));

  // Auto-sélectionner le premier compte disponible quand la liste change
  useEffect(() => {
    if (availableAccounts.length > 0 && !availableAccounts.find(a => a.id === destinationAccountId)) {
      setDestinationAccountId(availableAccounts[0].id);
    }
  }, [destinationType, availableAccounts, destinationAccountId]);

  const isCrossType = destinationType !== sourceType;

  if (!isOpen) return null;

  const handleTransfer = () => {
    if (!destinationAccountId) return;
    onTransfer(destinationType, destinationAccountId, recurringMode);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Titre */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('transferModal.title', { count: operationCount })}
          </h3>

          {/* Type de destination */}
          {hasCouple && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transferModal.accountType')}
              </label>
              <SegmentedControl
                block
                ariaLabel={t('transferModal.accountType')}
                value={destinationType}
                onChange={setDestinationType}
                options={[
                  { value: 'shared', label: t('transferModal.sharedAccount') },
                  { value: 'personal', label: t('transferModal.personalBudget') },
                ]}
              />
            </div>
          )}

          {/* S{'\u00e9'}lecteur de compte */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('transferModal.destinationAccount')}
            </label>
            {availableAccounts.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                {t('transferModal.noAccountAvailable')}
              </p>
            ) : (
              <select
                value={destinationAccountId}
                onChange={(e) => setDestinationAccountId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {availableAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Option r{'\u00e9'}currence */}
          {hasRecurring && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transferModal.recurringOperations')}
              </label>
              <SegmentedControl
                block
                wrap
                ariaLabel={t('transferModal.recurringOperations')}
                value={recurringMode}
                onChange={setRecurringMode}
                options={[
                  { value: 'single', label: t('transferModal.onlyTheseOccurrences') },
                  { value: 'all_future', label: t('transferModal.theseAndAllFuture') },
                ]}
              />
            </div>
          )}

          {/* Avertissement cross-type */}
          {isCrossType && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                {sourceType === 'shared'
                  ? t('transferModal.crossTypeWarningShared')
                  : t('transferModal.crossTypeWarningPersonal')}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              {tc('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleTransfer}
              disabled={!destinationAccountId || availableAccounts.length === 0 || isLoading}
              isLoading={isLoading}
            >
              {t('transferModal.transfer')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
